import {
  type FolderId,
  MAX_IMAGE_UPLOAD_MB,
  type PrincipalOptions,
  type ProjectMemberView,
  type ProjectView,
} from "@esi/shared";
import { FileUp, X } from "lucide-react";
import {
  type DragEvent,
  memo,
  type RefObject,
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { AppDialog } from "@/components/AppDialog";
import { Notice } from "@/components/Notice";
import { Button } from "@/components/ui/button";
import { DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import { formatBytes } from "../i18n/format.ts";
import { useSession } from "../session.tsx";
import { useUnsavedWork } from "../shell/session-ended.ts";
import {
  AccessFields,
  type AccessState,
  accessReaders,
  initialAccess,
  nothingPicked,
} from "./AccessFields.tsx";
import { canChange } from "./capabilities.ts";
import { FolderField } from "./FolderPicker.tsx";
import type { KnowledgeSettings } from "./queries.ts";
import {
  ACCEPTED_FILES,
  abandonUpload,
  confirmedDocument,
  resumeUpload,
  uploadDocument,
} from "./upload.ts";
import {
  accessEditable,
  addFiles,
  type BatchItem,
  type BatchRunner,
  blocked,
  createBatchRunner,
  findDuplicates,
  IN_FLIGHT,
  MAX_BATCH_FILES,
  percentOf,
  removable,
  sha256,
  unfinished,
} from "./upload-batch.ts";

export interface UploadFormProps {
  project: ProjectView;
  settings: KnowledgeSettings;
  /** Undefined while loading or when the list could not be loaded. */
  members: readonly ProjectMemberView[] | undefined;
  /** The tenant's groups and attribute rules; the `groups` audience shows only when there are some. */
  principals?: PrincipalOptions | undefined;
  /** Titles of the project files the user can read, for same-name warnings. */
  visibleTitles?: readonly string[] | undefined;
  /** Files reached the project: nothing is in flight any more. */
  onSettled?: () => void;
  /** Every file sent is uploaded and nothing is left to send. */
  onDone: () => void;
  onCancel: () => void;
  /** Receives the form's own cancel, so Escape in the dialog goes through it. */
  cancelRef?: RefObject<(() => void) | null>;
  /**
   * The Files folder the batch goes into (where the upload started); null is the root. Absent:
   * no folder field (a form outside a project's tree).
   */
  initialFolder?: FolderId | null;
}

/** Announcements one at a time, at most this often; a burst is summed up. */
const ANNOUNCE_EVERY_MS = 1500;

/**
 * Choose files (picker or drop), who may read them, their classification and origin, then
 * upload them: each file through its own intent, PUT and confirmation, a few at a time. Any
 * file can override the batch's access before it starts.
 */
export function UploadForm({
  project,
  settings,
  members,
  principals,
  visibleTitles,
  onSettled,
  onDone,
  onCancel,
  cancelRef,
  initialFolder,
}: UploadFormProps) {
  const { csrfToken, userId } = useSession();
  const id = useId();
  const { t } = useTranslation();
  const [access, setAccess] = useState<AccessState>(() =>
    initialAccess(project, settings, principals),
  );
  const accessRef = useRef(access);
  accessRef.current = access;
  // Files sent later in the batch go where the folder points then.
  const [folderId, setFolderId] = useState<FolderId | null>(initialFolder ?? null);
  const folderRef = useRef(folderId);
  folderRef.current = folderId;
  // The runner lives as long as the form: it reads the current session and project.
  const live = useRef({ csrfToken, userId, project });
  live.current = { csrfToken, userId, project };
  const [items, setItems] = useState<readonly BatchItem[]>([]);
  const onSettledRef = useRef(onSettled);
  onSettledRef.current = onSettled;
  const [runner] = useState<BatchRunner>(() =>
    createBatchRunner({
      onChange: setItems,
      // Also after the dialog closed: cancelled files settle (or are removed) later.
      onIdle: () => onSettledRef.current?.(),
      uploads: {
        start: (item, hooks) => {
          const { csrfToken, userId, project } = live.current;
          const effective = item.override ?? accessRef.current;
          return uploadDocument(
            {
              projectId: project.id,
              file: item.file,
              readers: accessReaders(effective, project, userId),
              classification: effective.classification,
              origin: effective.origin,
              folderId: folderRef.current,
            },
            csrfToken,
            hooks,
          );
        },
        resume: (item, pending, hooks) =>
          resumeUpload(pending, item.file, live.current.project.id, live.current.csrfToken, hooks),
        abandon: (pending) =>
          abandonUpload(pending, live.current.project.id, live.current.csrfToken),
        confirmed: (pending) => confirmedDocument(pending, live.current.project.id),
      },
    }),
  );
  // Leaving mid-batch (the page changed after the unsaved-work prompt): nobody is left to retry,
  // so stop what is unfinished and remove the documents it declared.
  useEffect(() => () => runner.cancelAll(), [runner]);

  const [dropped, setDropped] = useState(0);
  const [droppedDirectory, setDroppedDirectory] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [editing, setEditing] = useState<string | null>(null);
  const [confirmClose, setConfirmClose] = useState(false);

  const hashes = useRef(new WeakMap<File, Promise<string>>());
  const hashOf = useCallback((file: File) => {
    let hash = hashes.current.get(file);
    if (!hash) {
      hash = sha256(file);
      hashes.current.set(file, hash);
      // A failed read is not remembered: the next scan tries again.
      hash.catch(() => hashes.current.delete(file));
    }
    return hash;
  }, []);
  const titlesRef = useRef(visibleTitles ?? []);
  titlesRef.current = visibleTitles ?? [];
  // Nothing starts while a scan runs, and only the latest scan applies (files may change meanwhile).
  const scan = useRef(0);
  const [scanning, setScanning] = useState(false);
  async function refreshDuplicates() {
    scan.current += 1;
    const current = scan.current;
    setScanning(true);
    try {
      const issues = await findDuplicates(
        runner.items(),
        titlesRef.current,
        hashOf,
        () => current !== scan.current,
      );
      if (current === scan.current) {
        runner.setIssues(issues);
      }
    } catch {
      // A file that cannot be read now fails its own upload; the batch stays usable.
    } finally {
      if (current === scan.current) {
        setScanning(false);
      }
    }
  }

  function add(files: readonly File[]) {
    if (files.length === 0) {
      return;
    }
    let over = 0;
    runner.edit((current) => {
      const next = addFiles(current, files, settings.maxUploadMb);
      over = next.dropped;
      return next.items;
    });
    setDropped(over);
    void refreshDuplicates();
  }

  // Row actions through one stable callback, so a row re-renders only when its file changes.
  const rowAction = useRef<(action: RowAction, itemId: string) => void>(() => {});
  rowAction.current = (action, itemId) => {
    const item = runner.items().find((i) => i.id === itemId);
    if (!item) {
      return;
    }
    if (action === "remove") {
      remove(itemId);
    } else if (action === "cancel") {
      runner.cancel(itemId);
    } else if (action === "retry") {
      if (canRetry(item)) {
        runner.retry(itemId);
      }
    } else {
      setEditing(itemId);
    }
  };
  const onRowAction = useCallback(
    (action: RowAction, itemId: string) => rowAction.current(action, itemId),
    [],
  );

  // A file that will not be sent any more (cancelled, not sent, refused for good) is no
  // longer an original: copies of it are checked again.
  const settledCount = items.filter(
    (i) => i.status === "cancelled" || i.status === "not_sent" || i.final,
  ).length;
  const rescan = useRef(refreshDuplicates);
  rescan.current = refreshDuplicates;
  useEffect(() => {
    if (settledCount > 0) {
      void rescan.current();
    }
  }, [settledCount]);

  function remove(itemId: string) {
    runner.edit((current) => current.filter((i) => i.id !== itemId));
    void refreshDuplicates();
  }

  const effective = (item: BatchItem) => item.override ?? access;
  const sendable = (item: BatchItem) => !nothingPicked(effective(item));
  const active = items.some(unfinished);
  const ready = items.filter((i) => i.status === "ready" && !blocked(i) && sendable(i));
  // A declared document keeps the access it was declared with; otherwise the access must be set.
  const canRetry = (item: BatchItem) =>
    item.status === "failed" && !item.final && (item.pending !== undefined || sendable(item));
  const retryable = items.filter(canRetry);
  const started = items.filter((i) => i.status !== "ready");
  const unsent = items.filter(unfinished).length;
  // Chosen files, an upload in flight or a document to resume would be lost by a page change.
  useUnsavedWork(items.some((i) => i.status === "ready" || unfinished(i) || i.pending));

  // Each time the batch has nothing left in flight: refresh the list, and close when done.
  const wasActive = useRef(false);
  useEffect(() => {
    if (wasActive.current && !active) {
      const sent = items.filter((i) => i.status !== "ready");
      const left = items.some((i) => i.status === "ready" && !blocked(i));
      if (!left && sent.length > 0 && sent.every((i) => i.status === "done")) {
        onDone();
      }
    }
    wasActive.current = active;
  }, [active, items, onDone]);

  const summary =
    started.length > 0
      ? [
          t("upload.batch.summary", {
            done: started.filter((i) => i.status === "done").length,
            count: started.length,
          }),
          ...(["failed", "not_sent", "cancelled"] as const).flatMap((status) => {
            const count = started.filter((i) => i.status === status).length;
            const key =
              status === "failed"
                ? "upload.batch.failedCount"
                : status === "not_sent"
                  ? "upload.batch.notSentCount"
                  : "upload.batch.cancelledCount";
            return count > 0 ? [t(key, { count })] : [];
          }),
        ].join(" · ")
      : null;
  const announcement = useAnnouncements(items, active, summary);

  function submit() {
    if (active || scanning) {
      return;
    }
    if (ready.length > 0) {
      setDropped(0);
      setDroppedDirectory(false);
      runner.start(sendable);
      return;
    }
    for (const item of retryable) {
      runner.retry(item.id);
    }
  }

  function requestClose() {
    if (unsent > 0) {
      setConfirmClose(true);
      return;
    }
    // Failed files keep a declared document: remove it rather than leave it incomplete.
    runner.cancelAll();
    onCancel();
  }

  if (cancelRef) {
    cancelRef.current = requestClose;
  }

  function onDrop(e: DragEvent<HTMLElement>) {
    e.preventDefault();
    setDragging(false);
    if (active) {
      return;
    }
    const files: File[] = [];
    let sawFolder = false;
    const entries = e.dataTransfer.items;
    if (entries && entries.length > 0) {
      for (const entry of Array.from(entries)) {
        if (entry.kind !== "file") {
          continue;
        }
        // A dropped directory adds nothing: its files are not read, and it makes no folder.
        if (entry.webkitGetAsEntry?.()?.isDirectory) {
          sawFolder = true;
          continue;
        }
        const file = entry.getAsFile();
        if (file) {
          files.push(file);
        }
      }
    } else {
      files.push(...Array.from(e.dataTransfer.files));
    }
    setDroppedDirectory(sawFolder);
    add(files);
  }

  const onDrag = (e: DragEvent<HTMLElement>) => {
    e.preventDefault();
    if (!active) {
      e.dataTransfer.dropEffect = "copy";
      setDragging(true);
    }
  };

  // The batch's access applies to files not started yet; it is fixed while files are on their way.
  const batchLocked =
    active || (items.length > 0 && !items.some((i) => accessEditable(i) && !i.override));
  const editingItem = items.find((i) => i.id === editing);
  const primary =
    ready.length > 0 || retryable.length === 0
      ? t("upload.batch.submit", { count: Math.max(ready.length, 1) })
      : t("upload.batch.retryAll", { count: retryable.length });

  return (
    <form
      className="flex flex-col gap-6"
      aria-busy={active}
      onSubmit={(e) => {
        e.preventDefault();
        submit();
      }}
    >
      <div className="flex flex-col gap-1.5">
        <Label htmlFor={`${id}-files`}>{t("upload.batch.files")}</Label>
        <label
          className={cn(
            "flex cursor-pointer items-center gap-3 rounded-lg border border-dashed border-line-strong bg-surface-sunken px-3 py-4 transition-colors duration-(--duration-fast) hover:bg-surface-hover has-[:focus-visible]:border-focus has-[:disabled]:cursor-not-allowed has-[:disabled]:opacity-60",
            dragging && "border-focus bg-surface-hover",
          )}
          onDragEnter={onDrag}
          onDragOver={onDrag}
          onDragLeave={() => setDragging(false)}
          onDrop={onDrop}
        >
          <FileUp aria-hidden="true" className="size-5 shrink-0 text-fg-muted" strokeWidth={1.5} />
          <span className="min-w-0 flex-1 text-sm text-fg">
            {dragging ? (
              t("upload.batch.dropping")
            ) : (
              <>
                <span className="font-medium">{t("upload.batch.choose")}</span>{" "}
                <span className="text-fg-secondary">{t("upload.batch.dropHint")}</span>
              </>
            )}
          </span>
          <input
            id={`${id}-files`}
            type="file"
            multiple
            accept={ACCEPTED_FILES}
            disabled={active}
            aria-describedby={`${id}-files-hint`}
            className="sr-only"
            onChange={(e) => {
              const files = Array.from(e.currentTarget.files ?? []);
              // Cleared, so choosing the same file again is noticed.
              e.currentTarget.value = "";
              setDroppedDirectory(false);
              add(files);
            }}
          />
        </label>
        <p id={`${id}-files-hint`} className="text-xs text-fg-muted">
          {t("upload.fileHint", {
            mb: settings.maxUploadMb,
            imageMb: Math.min(settings.maxUploadMb, MAX_IMAGE_UPLOAD_MB),
          })}
        </p>
      </div>

      {/* After the files: the chooser stays the dialog's first control. */}
      {initialFolder !== undefined ? (
        <FolderField
          projectId={project.id}
          section="files"
          value={folderId}
          onChange={setFolderId}
          canCreate={canChange(project, "folder.manage")}
        />
      ) : null}
      {dropped > 0 ? (
        <Notice tone="warning">
          {t("upload.batch.limit", { count: dropped, max: MAX_BATCH_FILES })}
        </Notice>
      ) : null}
      {droppedDirectory ? <Notice tone="warning">{t("upload.batch.folder")}</Notice> : null}

      {items.length > 0 ? (
        <ul
          aria-label={t("upload.batch.list")}
          className="flex flex-col divide-y divide-line rounded-xl border border-line bg-surface-raised"
        >
          {items.map((item) => (
            <FileRow
              key={item.id}
              item={item}
              accessMissing={!blocked(item) && accessEditable(item) && !sendable(item)}
              onAction={onRowAction}
            />
          ))}
        </ul>
      ) : null}

      {summary ? (
        <p className="text-sm font-medium text-fg-secondary tabular-nums">{summary}</p>
      ) : null}
      <p role="status" aria-live="polite" className="sr-only">
        {announcement}
      </p>

      <div className="flex flex-col gap-6">
        {items.length > 1 ? (
          <p className="text-sm text-fg-secondary">{t("upload.batch.sharedAccess")}</p>
        ) : null}
        <AccessFields
          project={project}
          settings={settings}
          members={members}
          principals={principals}
          value={access}
          onChange={setAccess}
          disabled={batchLocked}
          fixedReader={{ id: userId, label: t("upload.you") }}
        />
      </div>

      <DialogFooter sticky>
        <Button type="button" variant="outline" onClick={requestClose}>
          {!active && items.some((i) => i.status === "done")
            ? t("upload.batch.close")
            : t("upload.cancel")}
        </Button>
        {active ? (
          <Button type="button" variant="outline" onClick={() => runner.cancelAll()}>
            {t("upload.batch.cancelAll")}
          </Button>
        ) : null}
        {/* Only marked unavailable while files are on their way, so focus stays on it. */}
        <Button
          type="submit"
          disabled={!active && (scanning || (ready.length === 0 && retryable.length === 0))}
          aria-disabled={active || undefined}
          className="aria-disabled:pointer-events-none aria-disabled:opacity-50"
        >
          {primary}
        </Button>
      </DialogFooter>

      <AccessDialog
        item={editingItem}
        batch={access}
        project={project}
        settings={settings}
        members={members}
        principals={principals}
        userId={userId}
        onClose={() => setEditing(null)}
        onSave={(override) => {
          if (editingItem && accessEditable(editingItem)) {
            runner.edit((current) =>
              current.map((i) => (i.id === editingItem.id ? { ...i, override } : i)),
            );
          }
          setEditing(null);
        }}
      />

      <AppDialog
        open={confirmClose}
        onClose={() => setConfirmClose(false)}
        title={t("upload.batch.closeTitle")}
        description={t("upload.batch.closeLead", { count: unsent })}
      >
        <DialogFooter>
          <Button variant="outline" onClick={() => setConfirmClose(false)}>
            {t("upload.batch.keepUploading")}
          </Button>
          <Button
            variant="destructive"
            onClick={() => {
              setConfirmClose(false);
              runner.cancelAll();
              onCancel();
            }}
          >
            {t("upload.batch.stopAndClose")}
          </Button>
        </DialogFooter>
      </AppDialog>
    </form>
  );
}

const STATUS_STYLE: Record<BatchItem["status"] | "skipped", { dot: string; text: string }> = {
  ready: { dot: "bg-line-strong", text: "text-fg-secondary" },
  skipped: { dot: "bg-danger-border", text: "text-danger-fg" },
  queued: { dot: "bg-line-strong", text: "text-fg-muted" },
  uploading: { dot: "bg-warning-border motion-safe:animate-pulse", text: "text-fg-secondary" },
  confirming: { dot: "bg-warning-border motion-safe:animate-pulse", text: "text-fg-secondary" },
  done: { dot: "bg-success-border", text: "text-success-fg" },
  failed: { dot: "bg-danger-border", text: "text-danger-fg" },
  cancelled: { dot: "bg-line-strong", text: "text-fg-muted" },
  not_sent: { dot: "bg-danger-border", text: "text-danger-fg" },
};

type RowAction = "remove" | "cancel" | "retry" | "access";

/** One file: name, size, status (with byte progress), its issues and the actions it allows. */
const FileRow = memo(function FileRow(props: {
  item: BatchItem;
  accessMissing: boolean;
  onAction: (action: RowAction, itemId: string) => void;
}) {
  const { item } = props;
  const { t } = useTranslation();
  const id = useId();
  const name = item.file.name;
  const skipped = item.status === "ready" && blocked(item);
  const percent = percentOf(item.progress);
  const status = skipped
    ? t("upload.batch.status.skipped")
    : item.status === "uploading"
      ? item.step === "put"
        ? t("upload.batch.status.put", { percent })
        : t("upload.batch.status.intent")
      : item.status === "confirming"
        ? t("upload.batch.status.confirm")
        : t(`upload.batch.status.${item.status}`);
  const style = STATUS_STYLE[skipped ? "skipped" : item.status];
  const messages = [
    ...item.issues.map((issue) => ({
      text: issue.message,
      tone: blocked({ issues: [issue] }) ? "danger" : "warning",
    })),
    ...(item.error ? [{ text: item.error, tone: "danger" }] : []),
    ...(props.accessMissing ? [{ text: t("upload.batch.accessMissing"), tone: "danger" }] : []),
  ];
  const describedBy = messages.length > 0 ? `${id}-messages` : undefined;
  // Not while confirming: the server decides, and a confirmed file stays.
  const cancellable = item.status === "queued" || item.status === "uploading";
  return (
    <li className="flex flex-col gap-1.5 px-3 py-2.5" aria-describedby={describedBy}>
      <div className="flex items-start gap-3">
        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
          <p className="truncate text-sm font-medium text-fg" title={name}>
            {name}
          </p>
          <p className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-fg-muted tabular-nums">
            <span className={cn("inline-flex items-center gap-1.5 font-medium", style.text)}>
              <span aria-hidden="true" className={cn("size-1.5 rounded-full", style.dot)} />
              {status}
            </span>
            <span>{formatBytes(item.file.size)}</span>
            {item.override ? <span>{t("upload.batch.customAccess")}</span> : null}
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-1">
          {accessEditable(item) && !skipped ? (
            <Button
              type="button"
              size="xs"
              variant="ghost"
              aria-label={t("upload.batch.changeAccessFile", { name })}
              onClick={() => props.onAction("access", item.id)}
            >
              {t("upload.batch.changeAccess")}
            </Button>
          ) : null}
          {item.status === "failed" && !item.final && !props.accessMissing ? (
            <Button
              type="button"
              size="xs"
              variant="ghost"
              aria-label={t("upload.batch.retryFile", { name })}
              aria-describedby={describedBy}
              onClick={() => props.onAction("retry", item.id)}
            >
              {t("upload.batch.retry")}
            </Button>
          ) : null}
          {removable(item) ? (
            <Button
              type="button"
              size="icon-xs"
              variant="ghost"
              aria-label={t("upload.batch.removeFile", { name })}
              onClick={() => props.onAction("remove", item.id)}
            >
              <X aria-hidden="true" />
            </Button>
          ) : cancellable ? (
            <Button
              type="button"
              size="icon-xs"
              variant="ghost"
              aria-label={t("upload.batch.cancelFile", { name })}
              onClick={() => props.onAction("cancel", item.id)}
            >
              <X aria-hidden="true" />
            </Button>
          ) : null}
        </div>
      </div>
      {IN_FLIGHT.has(item.status) ? (
        <progress
          aria-label={t("upload.batch.progressOf", { name })}
          max={item.progress.total || 1}
          value={item.status === "confirming" ? item.progress.total || 1 : item.progress.sent}
          className="h-1 w-full appearance-none overflow-hidden rounded-full bg-surface-sunken [&::-moz-progress-bar]:bg-action [&::-webkit-progress-bar]:bg-surface-sunken [&::-webkit-progress-value]:bg-action [&::-webkit-progress-value]:transition-[width]"
        />
      ) : null}
      {messages.length > 0 ? (
        <div id={`${id}-messages`} className="flex flex-col gap-0.5">
          {messages.map((m) => (
            <p
              key={m.text}
              className={cn("text-xs", m.tone === "danger" ? "text-danger-fg" : "text-warning-fg")}
            >
              {m.text}
            </p>
          ))}
        </div>
      ) : null}
    </li>
  );
});

/** The access of one file: the batch's until saved here, then its own. */
function AccessDialog(props: {
  item: BatchItem | undefined;
  batch: AccessState;
  project: ProjectView;
  settings: KnowledgeSettings;
  members: readonly ProjectMemberView[] | undefined;
  principals?: PrincipalOptions | undefined;
  userId: string;
  onClose: () => void;
  onSave: (override: AccessState | undefined) => void;
}) {
  const { item } = props;
  const { t } = useTranslation();
  return (
    <AppDialog
      open={item !== undefined}
      onClose={props.onClose}
      title={item ? t("upload.batch.accessTitle", { name: item.file.name }) : ""}
      description={t("upload.batch.accessLead")}
    >
      {item ? (
        <AccessEditor
          initial={item.override ?? props.batch}
          overridden={item.override !== undefined}
          project={props.project}
          settings={props.settings}
          members={props.members}
          principals={props.principals}
          userId={props.userId}
          onClose={props.onClose}
          onSave={props.onSave}
        />
      ) : null}
    </AppDialog>
  );
}

function AccessEditor(props: {
  initial: AccessState;
  overridden: boolean;
  project: ProjectView;
  settings: KnowledgeSettings;
  members: readonly ProjectMemberView[] | undefined;
  principals?: PrincipalOptions | undefined;
  userId: string;
  onClose: () => void;
  onSave: (override: AccessState | undefined) => void;
}) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState(props.initial);
  return (
    <div className="flex flex-col gap-6">
      <AccessFields
        project={props.project}
        settings={props.settings}
        members={props.members}
        principals={props.principals}
        value={draft}
        onChange={setDraft}
        fixedReader={{ id: props.userId, label: t("upload.you") }}
      />
      <DialogFooter>
        {props.overridden ? (
          <Button
            type="button"
            variant="ghost"
            className="sm:mr-auto"
            onClick={() => props.onSave(undefined)}
          >
            {t("upload.batch.accessReset")}
          </Button>
        ) : null}
        <Button type="button" variant="outline" onClick={props.onClose}>
          {t("upload.cancel")}
        </Button>
        <Button type="button" disabled={nothingPicked(draft)} onClick={() => props.onSave(draft)}>
          {t("upload.batch.accessSave")}
        </Button>
      </DialogFooter>
    </div>
  );
}

/**
 * The polite live region's text: files that finish, fail or are cancelled, then the batch's
 * summary when nothing is left in flight. At most one announcement per `ANNOUNCE_EVERY_MS`; a
 * burst is said as the summary, so a large batch does not flood the screen reader.
 */
function useAnnouncements(
  items: readonly BatchItem[],
  active: boolean,
  summary: string | null,
): string {
  const { t } = useTranslation();
  const [text, setText] = useState("");
  const seen = useRef(new Map<string, BatchItem["status"]>());
  const queue = useRef<string[]>([]);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const summaryRef = useRef(summary);
  summaryRef.current = summary;
  const wasActive = useRef(false);

  useEffect(
    () => () => {
      if (timer.current) {
        clearTimeout(timer.current);
      }
    },
    [],
  );

  useEffect(() => {
    const said: string[] = [];
    const present = new Set(items.map((i) => i.id));
    for (const id of seen.current.keys()) {
      if (!present.has(id)) {
        seen.current.delete(id);
      }
    }
    for (const item of items) {
      const before = seen.current.get(item.id);
      seen.current.set(item.id, item.status);
      if (before === item.status || before === undefined) {
        continue;
      }
      const name = item.file.name;
      if (item.status === "done") {
        said.push(t("upload.batch.announce.done", { name }));
      } else if (item.status === "failed") {
        said.push(t("upload.batch.announce.failed", { name, reason: item.error ?? "" }));
      } else if (item.status === "cancelled") {
        said.push(t("upload.batch.announce.cancelled", { name }));
      } else if (item.status === "not_sent") {
        said.push(t("upload.batch.announce.not_sent", { name }));
      }
    }
    const ended = wasActive.current && !active;
    wasActive.current = active;
    if (said.length === 0 && !ended) {
      return;
    }
    queue.current.push(...said);
    if (ended) {
      // The end of the batch closes with its summary, after the last files when they are few.
      const last = queue.current.length <= 2 ? queue.current : [];
      queue.current = [...last, summaryRef.current ?? ""];
    }
    const flush = () => {
      const waiting = queue.current;
      queue.current = [];
      if (waiting.length === 0) {
        timer.current = null;
        return;
      }
      setText(waiting.length > 3 ? (summaryRef.current ?? waiting.join(" ")) : waiting.join(" "));
      timer.current = setTimeout(flush, ANNOUNCE_EVERY_MS);
    };
    if (!timer.current) {
      flush();
    }
  }, [items, active, t]);

  return text;
}
